import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useApp } from "../context/AppContext";

export const ProcessingScreen = () => {
  const nav = useNavigate();
  const { activeTx, partner, updateActiveTx, persistTransaction } = useApp();
  const started = useRef(false);

  const STAGES = [
    { key: "INIT", label: "Initialising", duration: 500 },
    { key: "VALIDATED", label: "Validated", duration: 700 },
    { key: "ROUTED", label: "Routing via " + partner.name, duration: 800 },
    { key: "PROCESSING", label: "Processing via " + partner.name, duration: 1200 },
  ];

  useEffect(() => {
    if (!activeTx) {
      nav("/", { replace: true });
      return;
    }
    if (started.current) return;
    started.current = true;

    (async () => {
      for (const stage of STAGES) {
        updateActiveTx({ status: stage.key });
        await new Promise((r) => setTimeout(r, stage.duration));
      }
      // Simulate ~90% success, 10% failure
      // MAGIC TRIGGER: If amount ends in .99, it always fails (for testing)
      const isTestFailure = activeTx.amount.toString().endsWith(".99") || activeTx.amount === 9.99;
      const success = isTestFailure ? false : Math.random() > 0.1;

      // Play audio feedback
      try {
        const audio = new Audio(
          success
            ? "https://assets.mixkit.co/active_storage/sfx/2013/2013-preview.mp3"
            : "https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"
        );
        audio.play().catch(() => {}); // catch if browser blocks autoplay
      } catch (err) {}

      const finalStatus = success ? "COMPLETED" : "FAILED";
      const finalized = {
        ...activeTx,
        status: finalStatus,
        createdAt: activeTx.createdAt || Date.now(),
      };
      updateActiveTx({ status: finalStatus });
      persistTransaction(finalized);
      nav(success ? "/send/success" : "/send/failure", { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentStage = activeTx?.status || "INIT";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full flex flex-col items-center justify-center px-8 text-center"
      data-testid="processing-screen"
    >
      <div className="relative w-28 h-28 mb-8">
        <div className="absolute inset-0 rounded-full bg-gray-100 animate-pulse" />
        <div className="absolute inset-2 rounded-full bg-white flex items-center justify-center shadow-sm">
          <Loader2 className="w-10 h-10 text-gray-900 animate-spin" strokeWidth={2.2} />
        </div>
      </div>

      <h1 className="font-display text-[24px] font-bold text-gray-900 tracking-tight">
        Processing via {partner.name}…
      </h1>
      <p className="text-[13px] text-gray-500 mt-2 leading-snug max-w-[280px]">
        Your payment is being executed by <b>{partner.name}</b>. Please don't close the
        app.
      </p>

      {/* Progress steps */}
      <div className="w-full max-w-[280px] mt-10 space-y-2.5">
        {STAGES.map((s) => {
          const idx = STAGES.findIndex((x) => x.key === currentStage);
          const myIdx = STAGES.findIndex((x) => x.key === s.key);
          const done = myIdx < idx || currentStage === "COMPLETED";
          const active = myIdx === idx && currentStage !== "COMPLETED";
          return (
            <div
              key={s.key}
              data-testid={`stage-${s.key}`}
              className="flex items-center gap-3 text-[13px]"
            >
              <span
                className={
                  "w-2 h-2 rounded-full " +
                  (done
                    ? "bg-emerald-500"
                    : active
                    ? "bg-gray-900 animate-pulse"
                    : "bg-gray-200")
                }
              />
              <span
                className={
                  "font-medium " +
                  (done ? "text-gray-900" : active ? "text-gray-900" : "text-gray-400")
                }
              >
                {s.label}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-10 inline-flex items-center gap-2 text-[11px] text-gray-500">
        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
        Shwiip acts as a technical interface only.
      </div>
    </motion.div>
  );
};

export default ProcessingScreen;
